import React, { useEffect, useMemo, useState } from 'react';
import CalendarCharts from './CalendarCharts';
import GovernanceHubHeader from './components/GovernanceHubHeader';
import SmacRegister from './SmacRegister';
import HubCorrespondence from './HubCorrespondence';
import FilterBar from './components/FilterBar';
import KPISummaryCards from './components/KPISummaryCards';
import ItemCard from './components/ItemCard';
import ItemRow from './components/ItemRow';
import DetailPanel from './components/DetailPanel';
import DualProgramCalendar from './components/DualProgramCalendar';
import MyWorkStrip from './components/MyWorkStrip';
import EmptyState from './components/EmptyState';

/**
 * The Medicare-side twin of deleting a state contract: archives every
 * ingested (non-SMAC) calendar row for the org — vaulted, never destroyed —
 * so a fresh calendar upload starts clean. Two-step confirm, compliance only.
 */
function MedicareResetButton({ apiBaseUrl, medicareCount, onDone }) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  if (medicareCount === 0 && !message) return null;
  return (
    <span className="inline-flex items-center gap-2">
      {message && <span className="text-xs text-emerald-600">{message}</span>}
      {!confirming ? (
        <button
          type="button"
          onClick={() => { setMessage(''); setConfirming(true); }}
          className="px-3 py-1.5 rounded-lg border border-red-200 bg-white text-xs font-medium text-red-600 hover:bg-red-50"
          title="Archive every ingested Medicare calendar item (kept in the archive, never destroyed) so a fresh upload starts clean"
        >
          Remove the Medicare calendar ({medicareCount})
        </button>
      ) : (
        <>
          <button
            type="button"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                const res = await fetch(`${apiBaseUrl}/api/calendar/obligations/archive-all`, {
                  method: 'POST',
                  credentials: 'include',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ scope: 'medicare', reason: 'Medicare calendar removed for re-upload' }),
                });
                const data = await res.json().catch(() => ({}));
                if (!res.ok) throw new Error(data.error || 'Archive failed');
                setMessage(`Archived ${data.archived} Medicare item(s).`);
                if (onDone) await onDone();
              } catch (e) {
                setMessage(e.message || 'Archive failed');
              } finally {
                setBusy(false);
                setConfirming(false);
              }
            }}
            className="px-3 py-1.5 rounded-lg bg-red-600 text-white text-xs font-semibold hover:bg-red-700 disabled:opacity-60"
          >
            {busy ? 'Archiving…' : 'Yes — archive every Medicare item'}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => setConfirming(false)}
            className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-xs font-medium text-slate-600 hover:bg-slate-50"
          >
            Keep
          </button>
        </>
      )}
    </span>
  );
}

export default function CalendarPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [obligations, setObligations] = useState([]);
  const [sourceFilter, setSourceFilter] = useState('all'); // 'all' | 'calendar' | 'memo' | 'cap'
  const [activityFilter, setActivityFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [cmsFile, setCmsFile] = useState(null);
  const [previewItems, setPreviewItems] = useState([]);
  const [previewSummary, setPreviewSummary] = useState(null);
  const [previewError, setPreviewError] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewAuthority, setPreviewAuthority] = useState('');
  const [ingestType, setIngestType] = useState('CMS');
  const [showRawText, setShowRawText] = useState(false);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [isCompliance, setIsCompliance] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [assignmentFilterDeptId, setAssignmentFilterDeptId] = useState('');
  const [assigningId, setAssigningId] = useState(null);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [actionableUpdatingId, setActionableUpdatingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [guidanceByObligationId, setGuidanceByObligationId] = useState({});
  const [memos, setMemos] = useState([]);
  const [caps, setCaps] = useState([]);
  const [viewMode, setViewMode] = useState('agenda'); // 'agenda' | 'calendar' | 'board' | 'insights'
  const [activeKPI, setActiveKPI] = useState(null); // 'overdue' | 'due_soon' | 'awaiting' | 'in_progress' | 'completed'
  const [selectedItem, setSelectedItem] = useState(null);

  /** A deliverable was attached or removed (or the item completed with it): the list learns the new count and status. */
  const handleObligationPatched = (obligationId, patch) => {
    setObligations((prev) => prev.map((o) => (o.id === obligationId ? { ...o, ...patch } : o)));
  };
  const [embeddedUrl, setEmbeddedUrl] = useState(null); // memo or CAP URL to show in embedded view (keeps Core sidebar)
  const apiBaseUrl = process.env.REACT_APP_API_URL || '';

  // Operator decision 2026-07-21: the Hub shows OBLIGATIONS ONLY (Medicare
  // "CMS" + "State Medicaid (SMAC)"). Memos live in Mandate, CAPs in the CAP
  // module — pulling them in here created confusion. HIDDEN, not removed:
  // flip this to restore the unified feed.
  const HUB_OBLIGATIONS_ONLY = true;

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      try {
        const [obligationsRes, memosRes, capsRes, visibilityRes] = await Promise.all([
          fetch(`${apiBaseUrl}/api/calendar/obligations`, { credentials: 'include' }),
          HUB_OBLIGATIONS_ONLY ? Promise.resolve(null) : fetch(`${apiBaseUrl}/api/calendar/memos`, { credentials: 'include' }),
          HUB_OBLIGATIONS_ONLY ? Promise.resolve(null) : fetch(`${apiBaseUrl}/api/calendar/caps`, { credentials: 'include' }),
          fetch(`${apiBaseUrl}/api/calendar/visibility`, { credentials: 'include' })
        ]);
        if (isMounted) {
          if (!obligationsRes.ok) throw new Error('Failed to load calendar obligations');
          const obligationsData = await obligationsRes.json();
          setObligations(Array.isArray(obligationsData) ? obligationsData : []);
          const memosData = memosRes ? await memosRes.json().catch(() => []) : [];
          setMemos(Array.isArray(memosData) ? memosData : []);
          const capsData = capsRes ? await capsRes.json().catch(() => []) : [];
          setCaps(Array.isArray(capsData) ? capsData : []);
          const visData = visibilityRes.ok ? await visibilityRes.json().catch(() => ({})) : {};
          setIsCompliance(!!visData.isCompliance);
          setError('');
        }
      } catch (err) {
        if (isMounted) {
          setError(err.message || 'Failed to load calendar obligations');
          setObligations([]);
          setMemos([]);
          setCaps([]);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    load();
    return () => { isMounted = false; };
  }, [apiBaseUrl]);

  useEffect(() => {
    if (!isCompliance) return;
    let isMounted = true;
    fetch(`${apiBaseUrl}/api/departments`, { credentials: 'include' })
      .then((res) => res.ok ? res.json() : [])
      .then((data) => {
        if (isMounted && Array.isArray(data)) setDepartments(data);
      })
      .catch(() => {});
    return () => { isMounted = false; };
  }, [apiBaseUrl, isCompliance]);

  useEffect(() => {
    let isMounted = true;
    const url = isCompliance && assignmentFilterDeptId
      ? `${apiBaseUrl}/api/calendar/assignable-users?department_id=${encodeURIComponent(assignmentFilterDeptId)}`
      : `${apiBaseUrl}/api/calendar/assignable-users`;
    fetch(url, { credentials: 'include' })
      .then((res) => res.ok ? res.json() : { users: [] })
      .then((data) => {
        if (isMounted && Array.isArray(data.users)) setAssignableUsers(data.users);
      })
      .catch(() => {});
    return () => { isMounted = false; };
  }, [apiBaseUrl, isCompliance, assignmentFilterDeptId]);

  const DUE_SOON_DAYS = 14; // logic window; KPI label uses 7 for "Due in 7 Days"

  /**
   * Dates arrive as UTC-midnight strings ("2026-07-24" or "...T00:00:00.000Z").
   * Parsing those with new Date() lands on the PREVIOUS local day in the
   * Americas — read the calendar date digits directly instead.
   */
  const parseDateOnly = (value) => {
    if (!value) return null;
    const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const formatDate = (value) => {
    if (!value) return '—';
    const parsed = parseDateOnly(value);
    if (!parsed) return value;
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(parsed);
  };

  /** Display date: range "Feb 3 – 23, 2026" when start/end exist, else single due date. */
  const formatDateDisplay = (item) => {
    if (item.start_date && item.end_date) return `${formatDate(item.start_date)} – ${formatDate(item.end_date)}`;
    return formatDate(item.due_date);
  };

  /** Phase A: effective status from due_date (or end_date for ranges) + display_status. Memos: use status or due_date. CAP: use display_status or due_date. */
  const getEffectiveStatus = (item) => {
    if (item.source === 'memo') {
      if ((item.status || '').toLowerCase() === 'completed') return 'completed';
      const due = item.due_date ? parseDateOnly(item.due_date) : null;
      if (!due || Number.isNaN(due.getTime())) return 'upcoming';
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const dueOnly = new Date(due);
      dueOnly.setHours(0, 0, 0, 0);
      if (dueOnly < today) return 'overdue';
      const windowEnd = new Date(today);
      windowEnd.setDate(windowEnd.getDate() + DUE_SOON_DAYS);
      if (dueOnly <= windowEnd) return 'due_soon';
      return 'upcoming';
    }
    if (item.source === 'cap') {
      const stored = (item.display_status || '').toLowerCase();
      if (stored === 'completed' || stored === 'satisfied') return 'completed';
      const due = item.due_date ? parseDateOnly(item.due_date) : null;
      if (!due || Number.isNaN(due.getTime())) return 'upcoming';
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const dueOnly = new Date(due);
      dueOnly.setHours(0, 0, 0, 0);
      if (dueOnly < today) return 'overdue';
      const windowEnd = new Date(today);
      windowEnd.setDate(windowEnd.getDate() + DUE_SOON_DAYS);
      if (dueOnly <= windowEnd) return 'due_soon';
      return 'upcoming';
    }
    const stored = (item.display_status || '').toLowerCase();
    if (stored === 'completed' || stored === 'satisfied' || stored === 'remediated') return 'completed';
    if (stored === 'no_action_needed' || stored === 'no action needed') return 'no_action_needed';
    if (stored === 'in_progress') return 'in_progress';
    const due = (item.due_date || item.end_date) ? parseDateOnly(item.due_date || item.end_date) : null;
    if (!due || Number.isNaN(due.getTime())) return 'upcoming';
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dueOnly = new Date(due);
    dueOnly.setHours(0, 0, 0, 0);
    if (dueOnly < today) return 'overdue';
    const windowEnd = new Date(today);
    windowEnd.setDate(windowEnd.getDate() + DUE_SOON_DAYS);
    if (dueOnly <= windowEnd) return 'due_soon';
    return 'upcoming';
  };

  const statusStyle = (effectiveStatus) => {
    const n = (effectiveStatus || '').toLowerCase();
    if (n === 'overdue') return 'bg-red-100 text-red-700 border border-red-200';
    if (n === 'due_soon') return 'bg-amber-100 text-amber-800 border border-amber-200';
    if (n === 'completed') return 'bg-slate-100 text-slate-600 border border-slate-200';
    if (n === 'no_action_needed') return 'bg-slate-100 text-slate-600 border border-slate-200';
    if (n === 'in_progress') return 'bg-sky-100 text-sky-700 border border-sky-200';
    return 'bg-blue-50 text-blue-700 border border-blue-200';
  };

  const effectiveStatusLabel = (effectiveStatus) => {
    const n = (effectiveStatus || '').toLowerCase();
    if (n === 'overdue') return 'Overdue';
    if (n === 'due_soon') return 'Due soon';
    if (n === 'completed') return 'Completed';
    if (n === 'no_action_needed') return 'No action needed';
    if (n === 'in_progress') return 'In progress';
    return 'Upcoming';
  };

  /** For calendar obligations: show Remediated when display_status is remediated, else use effective label */
  const calendarStatusLabel = (item) => {
    if (item.source !== 'calendar') return effectiveStatusLabel(getEffectiveStatus(item));
    const stored = (item.display_status || '').toLowerCase();
    if (stored === 'remediated') return 'Remediated';
    return effectiveStatusLabel(getEffectiveStatus(item));
  };

  /** Memos with source for display. */
  const memosWithSource = useMemo(() => (memos || []).map((m) => ({ ...m, source: 'memo' })), [memos]);

  /** CAP items with source for display. */
  const capsWithSource = useMemo(() => (caps || []).map((c) => ({ ...c, source: 'cap' })), [caps]);

  /** Calendar obligations with source for display. */
  const obligationsWithSource = useMemo(() => (obligations || []).map((o) => ({ ...o, source: 'calendar' })), [obligations]);

  // The open drawer shows the row as the list now has it (status, count),
  // not the snapshot taken when it was clicked.
  useEffect(() => {
    setSelectedItem((cur) => {
      if (!cur || (cur.source && cur.source !== 'calendar')) return cur;
      const fresh = obligationsWithSource.find((o) => o.id === cur.id);
      return fresh ? { ...cur, ...fresh } : cur;
    });
  }, [obligationsWithSource]);

  const obligationTypes = useMemo(() => {
    const fromObligations = (obligations || []).map((o) => o.obligation_type || o.type || '').filter(Boolean);
    const fromMemos = (memos || []).map((m) => m.classification || 'Memo').filter(Boolean);
    const fromCaps = (caps || []).map((c) => c.obligation_type || 'CAP').filter(Boolean);
    return Array.from(new Set([...fromObligations, ...fromMemos, ...fromCaps])).sort();
  }, [obligations, memos, caps]);

  /** Unified activity list for searchable combobox (100+ CMS activities). */
  const activities = useMemo(() => obligationTypes, [obligationTypes]);

  const getItemActivity = (item) => {
    if (item.source === 'memo') return item.classification || 'Memo';
    if (item.source === 'cap') return item.obligation_type || 'CAP';
    return item.obligation_type || item.type || '';
  };

  /** Calendar only: non-actionable items are excluded from overdue/due-soon and all metrics; shown in green. */
  const isCalendarNonActionable = (item) => item.source === 'calendar' && item.actionable === false;

  const sortByStatusAndDate = (a, b) => {
    const statusOrder = { overdue: 0, due_soon: 1, upcoming: 2, in_progress: 3, completed: 4, no_action_needed: 5 };
    const sa = getEffectiveStatus(a);
    const sb = getEffectiveStatus(b);
    const orderA = statusOrder[sa] ?? 2;
    const orderB = statusOrder[sb] ?? 2;
    if (orderA !== orderB) return orderA - orderB;
    const dueA = (a.due_date || a.end_date) ? new Date(a.due_date || a.end_date).getTime() : Number.MAX_SAFE_INTEGER;
    const dueB = (b.due_date || b.end_date) ? new Date(b.due_date || b.end_date).getTime() : Number.MAX_SAFE_INTEGER;
    return dueA - dueB;
  };

  const filteredMemoItems = useMemo(() => {
    if (sourceFilter === 'calendar' || sourceFilter === 'cap') return [];
    const filtered = memosWithSource.filter((item) => {
      const effective = getEffectiveStatus(item);
      const itemType = item.classification || 'Memo' || '';
      const activity = getItemActivity(item);
      const sourceMatch = sourceFilter === 'all' || sourceFilter === 'memo';
      const statusMatch = statusFilter === 'all' || effective === statusFilter;
      const typeMatch = typeFilter === 'all' || itemType === typeFilter;
      const activityMatch = !activityFilter || activity === activityFilter;
      return sourceMatch && statusMatch && typeMatch && activityMatch;
    });
    return [...filtered].sort(sortByStatusAndDate);
  }, [memosWithSource, sourceFilter, statusFilter, typeFilter, activityFilter]);

  const filteredCalendarObligations = useMemo(() => {
    if (sourceFilter === 'memo' || sourceFilter === 'cap') return [];
    const filtered = obligationsWithSource.filter((item) => {
      const effective = getEffectiveStatus(item);
      const itemType = item.obligation_type || item.type || '';
      const activity = getItemActivity(item);
      const sourceMatch = sourceFilter === 'all' || sourceFilter === 'calendar';
      const statusMatch = statusFilter === 'all' || effective === statusFilter;
      const typeMatch = typeFilter === 'all' || itemType === typeFilter;
      const activityMatch = !activityFilter || activity === activityFilter;
      return sourceMatch && statusMatch && typeMatch && activityMatch;
    });
    return [...filtered].sort(sortByStatusAndDate);
  }, [obligationsWithSource, sourceFilter, statusFilter, typeFilter, activityFilter]);

  const filteredCapItems = useMemo(() => {
    if (sourceFilter === 'memo' || sourceFilter === 'calendar') return [];
    const filtered = capsWithSource.filter((item) => {
      const effective = getEffectiveStatus(item);
      const itemType = item.obligation_type || 'CAP';
      const activity = getItemActivity(item);
      const sourceMatch = sourceFilter === 'all' || sourceFilter === 'cap';
      const statusMatch = statusFilter === 'all' || effective === statusFilter;
      const typeMatch = typeFilter === 'all' || itemType === typeFilter;
      const activityMatch = !activityFilter || activity === activityFilter;
      return sourceMatch && statusMatch && typeMatch && activityMatch;
    });
    return [...filtered].sort(sortByStatusAndDate);
  }, [capsWithSource, sourceFilter, statusFilter, typeFilter, activityFilter]);

  /** Unified list for card/board views: memos + caps + calendar obligations */
  const filteredAllItems = useMemo(() => {
    const base = [...filteredMemoItems, ...filteredCapItems, ...filteredCalendarObligations];
    if (activeKPI) {
      return base.filter((item) => {
        const effective = getEffectiveStatus(item);
        if (activeKPI === 'overdue') return effective === 'overdue';
        if (activeKPI === 'due_soon') return effective === 'due_soon';
        if (activeKPI === 'awaiting') return item.source === 'calendar' && !item.assigned_to_user_id && (item.assignment_mode || 'single_user') !== 'department_all';
        if (activeKPI === 'in_progress') return effective === 'in_progress';
        if (activeKPI === 'completed') return effective === 'completed';
        return true;
      });
    }
    return base;
  }, [filteredMemoItems, filteredCapItems, filteredCalendarObligations, activeKPI]);

  /** KPI counts from filtered dataset so cards reflect current filters. */
  const kpiCounts = useMemo(() => {
    const all = [...filteredMemoItems, ...filteredCapItems, ...filteredCalendarObligations];
    let overdue = 0, dueSoon = 0, awaiting = 0, inProgress = 0, completedThisMonth = 0;
    all.forEach((item) => {
      const effective = getEffectiveStatus(item);
      if (effective === 'overdue') overdue += 1;
      else if (effective === 'due_soon') dueSoon += 1;
      else if (effective === 'in_progress') inProgress += 1;
      else if (effective === 'completed') completedThisMonth += 1;
      if (item.source === 'calendar' && !item.assigned_to_user_id && (item.assignment_mode || 'single_user') !== 'department_all') awaiting += 1;
    });
    return { overdue, dueSoon, awaiting, inProgress, completedThisMonth };
  }, [filteredMemoItems, filteredCapItems, filteredCalendarObligations]);

  const hasActiveFilters = sourceFilter !== 'all' || activityFilter || statusFilter !== 'all' || typeFilter !== 'all' || activeKPI;

  const handleClearFilters = () => {
    setSourceFilter('all');
    setActivityFilter('');
    setStatusFilter('all');
    setTypeFilter('all');
    setActiveKPI(null);
  };

  /** Non-compliance: count of items requiring user action (overdue + due_soon + in_progress). */
  const myWorkCount = useMemo(() => {
    if (isCompliance) return 0;
    const all = [...filteredMemoItems, ...filteredCapItems, ...filteredCalendarObligations];
    return all.filter((item) => {
      const s = getEffectiveStatus(item);
      return s === 'overdue' || s === 'due_soon' || s === 'in_progress';
    }).length;
  }, [isCompliance, filteredMemoItems, filteredCapItems, filteredCalendarObligations, getEffectiveStatus]);

  const acceptedExactPreview = useMemo(
    () => previewItems.filter((item) => !item.rejected && !item.needs_manual_date),
    [previewItems]
  );

  const acceptedManualPreview = useMemo(
    () => previewItems.filter((item) => !item.rejected && item.needs_manual_date),
    [previewItems]
  );

  const rejectedPreview = useMemo(
    () => previewItems.filter((item) => item.rejected),
    [previewItems]
  );

  const handleSaveReviewed = async () => {
    const accepted = acceptedExactPreview.map((item) => ({
      title: item.title || item.normalized_title || '',
      due_date: item.due_date || null,
      start_date: item.start_date ?? null,
      end_date: item.end_date ?? null,
      authority: item.authority,
      source_document: item.source_document,
      source_page_or_row_ref: item.source_page_or_row_ref,
      // The calendar's own words — the server stores these as source_text so
      // the item panel can show exactly what the source document says.
      notes: item.notes ?? null,
      raw_activity_text: item.raw_activity_text ?? null,
      raw_date_text: item.raw_date_text ?? null
    }));
    const accepted_no_date = acceptedManualPreview.map((item) => ({
      title: item.title || item.normalized_title || '',
      due_date: null,
      start_date: item.start_date ?? null,
      end_date: item.end_date ?? null,
      authority: item.authority,
      source_document: item.source_document,
      source_page_or_row_ref: item.source_page_or_row_ref,
      notes: item.notes ?? null,
      raw_activity_text: item.raw_activity_text ?? null,
      raw_date_text: item.raw_date_text ?? null
    }));
    if (accepted.length === 0 && accepted_no_date.length === 0) {
      setSaveMessage('No items to save.');
      return;
    }
    const source_document = (accepted[0] || accepted_no_date[0])?.source_document || '';
    const authority = previewAuthority || ingestType;

    setSaving(true);
    setSaveMessage('');
    setError('');
    try {
      const res = await fetch(`${apiBaseUrl}/api/calendar/ingest/save`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accepted, accepted_no_date, source_document, authority })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || data.details?.join?.(', ') || 'Save failed');
      }
      setSaveMessage(`Saved ${data.saved} obligation(s).${data.failed > 0 ? ` ${data.failed} failed.` : ''}`);
      if (data.saved > 0) {
        setPreviewItems([]);
        setPreviewSummary(null);
        const listRes = await fetch(`${apiBaseUrl}/api/calendar/obligations`, { credentials: 'include' });
        if (listRes.ok) {
          const list = await listRes.json();
          setObligations(Array.isArray(list) ? list : []);
        }
      }
      if (data.errors?.length) setError(data.errors.join(' '));
    } catch (e) {
      setError(e.message || 'Failed to save');
      setSaveMessage('');
    } finally {
      setSaving(false);
    }
  };

  /** Calendar obligation: is it marked complete (display_status)? Used for checkmark. */
  const isCalendarObligationCompleted = (item) => {
    const s = (item.display_status || '').toLowerCase();
    return s === 'completed' || s === 'remediated' || s === 'satisfied';
  };

  /** Compliance-only: update assignment (mode, department, user). */
  const handleAssignmentChange = async (obligationId, payload) => {
    if (!isCompliance) return;
    setAssigningId(obligationId);
    try {
      const res = await fetch(`${apiBaseUrl}/api/calendar/obligations/${obligationId}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Update failed');
      }
      const updated = await res.json();
      setObligations((prev) =>
        prev.map((o) =>
          o.id === obligationId
            ? {
                ...o,
                assigned_to_user_id: updated.assigned_to_user_id,
                assignee_name: updated.assignee_name,
                assignee_email: updated.assignee_email,
                responsible_department_id: updated.responsible_department_id,
                responsible_department_name: updated.responsible_department_name,
                assignment_mode: updated.assignment_mode
              }
            : o
        )
      );
    } catch (e) {
      setError(e.message || 'Failed to update assignment');
    } finally {
      setAssigningId(null);
    }
  };

  /** Render Accountable UI for a calendar obligation. Compliance: full assignment controls. Non-compliance: read-only. */
  const renderObligationAccountable = (item, compact = false) => {
    if (!isCompliance) {
      const mode = (item.assignment_mode || 'single_user').toLowerCase();
      const text = mode === 'department_all' && item.responsible_department_name
        ? `All in ${item.responsible_department_name}`
        : (item.assignee_name || item.assignee_email || '—');
      return <span className={compact ? 'text-xs text-slate-700' : 'text-sm text-slate-700'}>{text}</span>;
    }
    const mode = (item.assignment_mode || 'single_user').toLowerCase();
    const baseClass = compact ? 'text-xs' : 'text-sm';
    return (
      <div className="space-y-1.5">
        <select
          value={mode}
          onChange={(e) => {
            const m = e.target.value;
            if (m === 'department_all') {
              const deptId = item.responsible_department_id || (departments[0]?.id ?? null);
              if (deptId) handleAssignmentChange(item.id, { assignment_mode: 'department_all', responsible_department_id: deptId, assigned_to_user_id: null });
            } else {
              handleAssignmentChange(item.id, { assignment_mode: 'single_user', assigned_to_user_id: item.assigned_to_user_id || null });
            }
          }}
          disabled={assigningId === item.id}
          className={`${baseClass} bg-white border border-slate-200 rounded px-2 py-1 w-full`}
        >
          <option value="single_user">Single user</option>
          <option value="department_all">All in department</option>
        </select>
        {mode === 'department_all' ? (
          <select
            value={item.responsible_department_id || ''}
            onChange={(e) => {
              const did = e.target.value || null;
              handleAssignmentChange(item.id, { assignment_mode: 'department_all', responsible_department_id: did });
            }}
            disabled={assigningId === item.id}
            className={`${baseClass} bg-white border border-slate-200 rounded px-2 py-1 w-full`}
          >
            <option value="">— Select department</option>
            {departments.map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
        ) : (
          <>
            <select
              value={assignmentFilterDeptId}
              onChange={(e) => setAssignmentFilterDeptId(e.target.value || '')}
              className={`${baseClass} bg-white border border-slate-200 rounded px-2 py-1 w-full`}
              title="Filter users by department"
            >
              <option value="">All departments</option>
              {departments.map((d) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
            <select
              value={item.assigned_to_user_id || ''}
              onChange={(e) => handleAssignmentChange(item.id, { assignment_mode: 'single_user', assigned_to_user_id: e.target.value || null })}
              disabled={assigningId === item.id}
              className={`${baseClass} bg-white border border-slate-200 rounded px-2 py-1 w-full`}
            >
              <option value="">— Unassigned</option>
              {assignableUsers.map((u) => (
                <option key={u.id} value={u.id}>{u.name || u.email || u.id}</option>
              ))}
            </select>
          </>
        )}
      </div>
    );
  };

  const handleStatusChange = async (obligationId, newDisplayStatus) => {
    setStatusUpdatingId(obligationId);
    try {
      const res = await fetch(`${apiBaseUrl}/api/calendar/obligations/${obligationId}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ display_status: newDisplayStatus })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Update failed');
      }
      const updated = await res.json();
      setObligations((prev) =>
        prev.map((o) =>
          o.id === obligationId ? { ...o, display_status: updated.display_status } : o
        )
      );
    } catch (e) {
      setError(e.message || 'Failed to update status');
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const handleActionableChange = async (obligationId, actionable) => {
    setActionableUpdatingId(obligationId);
    try {
      const res = await fetch(`${apiBaseUrl}/api/calendar/obligations/${obligationId}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ actionable: !!actionable })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Update failed');
      }
      const updated = await res.json();
      setObligations((prev) =>
        prev.map((o) => (o.id === obligationId ? { ...o, actionable: updated.actionable } : o))
      );
    } catch (e) {
      setError(e.message || 'Failed to update actionable');
    } finally {
      setActionableUpdatingId(null);
    }
  };

  const handleFetchGuidance = async (obligationId) => {
    setGuidanceByObligationId((prev) => ({
      ...prev,
      [obligationId]: { ...prev[obligationId], loading: true }
    }));
    try {
      const res = await fetch(`${apiBaseUrl}/api/calendar/obligations/${obligationId}/guidance`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Failed to get guidance');
      }
      setObligations((prev) =>
        prev.map((o) => {
          if (o.id !== obligationId) return o;
          return {
            ...o,
            title: data.title !== undefined ? String(data.title) : (o.title ?? ''),
            ai_guidance_status: data.status,
            ai_guidance_actions: data.actions ?? [],
            ai_guidance_by_whom: data.byWhom ?? '',
            ai_guidance_message: data.message ?? '',
            ai_guidance_certain: data.certain === true,
            ai_guidance_interpretation_type: data.interpretationType ?? null
          };
        })
      );
    } catch (e) {
      setGuidanceByObligationId((prev) => ({
        ...prev,
        [obligationId]: {
          ...prev[obligationId],
          loading: false,
          error: e.message || 'Failed to get guidance'
        }
      }));
      return;
    } finally {
      setGuidanceByObligationId((prev) => ({
        ...prev,
        [obligationId]: { ...prev[obligationId], loading: false }
      }));
    }
  };

  const handlePreviewUpload = async (event) => {
    event.preventDefault();
    setPreviewError('');
    setPreviewItems([]);
    setPreviewSummary(null);
    setPreviewAuthority('');

    if (!cmsFile) {
      setPreviewError('Please select a file to upload.');
      return;
    }

    const formData = new FormData();
    formData.append('file', cmsFile);

    const endpoint = ingestType === 'MEDICAID'
      ? `${apiBaseUrl}/api/calendar/ingest/medicaid/preview`
      : `${apiBaseUrl}/api/calendar/ingest/cms/preview`;

    setPreviewLoading(true);

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      const payload = await response.json().catch(() => ({}));

      if (payload.error) {
        setPreviewError(payload.error);
        setPreviewItems([]);
        setPreviewSummary(null);
        return;
      }

      if (!response.ok) {
        setPreviewError(payload.message || payload.error || 'Failed to generate preview');
        setPreviewItems([]);
        return;
      }

      const items = Array.isArray(payload.items) ? payload.items : [];
      setPreviewItems(items);
      setPreviewSummary(payload.summary || null);
      setPreviewAuthority(ingestType);
      setPreviewError('');
    } catch (err) {
      setPreviewError(err.message || 'Failed to generate preview');
      setPreviewItems([]);
      setPreviewSummary(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const onOpenInEmbeddedView = (url) => {
    setSelectedItem(null);
    setEmbeddedUrl(url);
  };

  if (embeddedUrl) {
    return (
      <div className="h-full min-h-0 flex flex-col overflow-hidden">
        <div className="flex-shrink-0 flex items-center gap-3 px-4 py-2 bg-white border-b border-slate-200">
          <button
            type="button"
            onClick={() => setEmbeddedUrl(null)}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium text-sm"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
            Back to Governance Hub
          </button>
        </div>
        <div className="flex-1 min-h-0 overflow-hidden">
          <iframe
            title="Memo or CAP"
            src={embeddedUrl}
            className="w-full h-full border-0"
            sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-modals"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="h-full bg-slate-50 min-h-screen p-6 overflow-y-auto">
      <div className="max-w-6xl mx-auto space-y-6">
        <GovernanceHubHeader
          viewMode={viewMode}
          setViewMode={setViewMode}
          showStateContracts={isCompliance}
        />

        {viewMode === 'state-contracts' && isCompliance && (
          <SmacRegister />
        )}

        {viewMode === 'correspondence' && isCompliance && (
          <HubCorrespondence />
        )}

        {viewMode !== 'state-contracts' && viewMode !== 'correspondence' && (
        <div className="space-y-3">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500">At a glance</h2>
          <KPISummaryCards
            overdueCount={kpiCounts.overdue}
            dueSoonCount={kpiCounts.dueSoon}
            awaitingAssignmentCount={kpiCounts.awaiting}
            inProgressCount={kpiCounts.inProgress}
            completedThisMonthCount={kpiCounts.completedThisMonth}
            activeKPI={activeKPI}
            onKPIClick={(id) => setActiveKPI((prev) => (prev === id ? null : id))}
            isCompliance={isCompliance}
          />
        </div>
        )}

        {viewMode !== 'state-contracts' && viewMode !== 'correspondence' && (
        <FilterBar
          sourceFilter={sourceFilter}
          setSourceFilter={setSourceFilter}
          showSource={!HUB_OBLIGATIONS_ONLY}
          activityFilter={activityFilter}
          setActivityFilter={setActivityFilter}
          activities={activities}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          typeFilter={typeFilter}
          setTypeFilter={setTypeFilter}
          obligationTypes={obligationTypes}
          onClearFilters={handleClearFilters}
          DUE_SOON_DAYS={7}
          hasActiveFilters={!!hasActiveFilters}
        />
        )}

        {viewMode !== 'state-contracts' && viewMode !== 'correspondence' && !isCompliance && myWorkCount > 0 && (
          <MyWorkStrip count={myWorkCount} />
        )}

        {viewMode === 'insights' && (
          <div className="mb-6">
            <CalendarCharts
              filteredItems={filteredAllItems}
              obligations={obligations}
              memos={memos}
              caps={caps}
              getEffectiveStatus={getEffectiveStatus}
              DUE_SOON_DAYS={DUE_SOON_DAYS}
            />
          </div>
        )}

        {(viewMode === 'agenda' || viewMode === 'calendar' || viewMode === 'board') && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200/80 p-6">
          {loading && (
            <div className="text-slate-500 py-12 text-center">Loading obligations...</div>
          )}

          {!loading && error && (
            <div className="text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm">{error}</div>
          )}

          {!loading && !error && filteredAllItems.length === 0 && (
            <EmptyState
              variant={obligations.length === 0 && memos.length === 0 && caps.length === 0 ? 'no-data' : 'no-results'}
              onClearFilters={hasActiveFilters ? handleClearFilters : undefined}
            />
          )}

          {!loading && !error && filteredAllItems.length > 0 && (
            <>
              {viewMode === 'agenda' && (
                <div className="space-y-6">
                  {(() => {
                    // The list is the schedule: urgency buckets first, each a
                    // clean run of rows. Scanning replaces hunting.
                    const now = new Date(); now.setHours(0, 0, 0, 0);
                    const in7 = new Date(now.getTime() + 7 * 86400000);
                    const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
                    const buckets = [
                      { key: 'overdue', label: 'Overdue', cls: 'text-red-600' },
                      { key: 'week', label: 'Next 7 days', cls: 'text-amber-600' },
                      { key: 'month', label: 'Later this month', cls: 'text-slate-600' },
                      { key: 'later', label: 'Further out', cls: 'text-slate-600' },
                      { key: 'nodate', label: 'No date yet', cls: 'text-slate-500' },
                      { key: 'done', label: 'Done', cls: 'text-slate-400' },
                    ];
                    const byBucket = { overdue: [], week: [], month: [], later: [], nodate: [], done: [] };
                    filteredAllItems.forEach((item) => {
                      const st = getEffectiveStatus(item);
                      if (st === 'completed' || st === 'no_action_needed') return byBucket.done.push(item);
                      const raw = item.due_date || item.end_date || item.start_date;
                      const dd = raw ? new Date(raw) : null;
                      if (!dd || Number.isNaN(dd.getTime())) return byBucket.nodate.push(item);
                      if (st === 'overdue') return byBucket.overdue.push(item);
                      if (dd <= in7) return byBucket.week.push(item);
                      if (dd <= monthEnd) return byBucket.month.push(item);
                      return byBucket.later.push(item);
                    });
                    return buckets.filter((b) => byBucket[b.key].length > 0).map((b) => (
                      <div key={b.key}>
                        <h3 className={`text-xs font-semibold uppercase tracking-wider mb-2 ${b.cls}`}>
                          {b.label} ({byBucket[b.key].length})
                        </h3>
                        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                          {byBucket[b.key].map((item) => {
                            const rowKey = item.memo_id ? `memo-${item.memo_id}` : item.id || (item.cap_id ? `cap-${item.cap_id}-${item.due_date}` : `${item.title}-${item.due_date}`);
                            return (
                              <ItemRow key={rowKey} item={item} effectiveStatus={getEffectiveStatus(item)}
                                onSelect={setSelectedItem} calendarStatusLabel={calendarStatusLabel} />
                            );
                          })}
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              )}

              {viewMode === 'calendar' && (
                <DualProgramCalendar
                  items={filteredAllItems}
                  parseDateOnly={parseDateOnly}
                  getEffectiveStatus={getEffectiveStatus}
                  onSelect={setSelectedItem}
                />
              )}

              {viewMode === 'board' && (
                <div className="flex gap-4 overflow-x-auto pb-4">
                  {['overdue', 'due_soon', 'in_progress', 'upcoming', 'completed', 'no_action_needed'].map((colStatus) => {
                    const colItems = filteredAllItems.filter((item) => getEffectiveStatus(item) === colStatus);
                    const colLabels = { overdue: 'Overdue', due_soon: 'Due soon', in_progress: 'In progress', upcoming: 'Upcoming', completed: 'Completed', no_action_needed: 'No action needed' };
                    return (
                      <div key={colStatus} className="flex-shrink-0 w-72 bg-slate-50 rounded-xl border border-slate-200 p-3">
                        <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">
                          {colLabels[colStatus]} ({colItems.length})
                        </h3>
                        <div className="space-y-3 max-h-[70vh] overflow-y-auto">
                          {colItems.map((item) => {
                            const rowKey = item.memo_id ? `memo-${item.memo_id}` : item.id || (item.cap_id ? `cap-${item.cap_id}-${item.due_date}` : `${item.title}-${item.due_date}`);
                            return (
                              <ItemCard
                                key={rowKey}
                                item={item}
                                effectiveStatus={getEffectiveStatus(item)}
                                formatDateDisplay={formatDateDisplay}
                                onSelect={setSelectedItem}
                                onOpenInEmbeddedView={onOpenInEmbeddedView}
                                isCompliance={isCompliance}
                                renderObligationAccountable={renderObligationAccountable}
                                isCalendarNonActionable={isCalendarNonActionable}
                                isCalendarObligationCompleted={isCalendarObligationCompleted}
                                handleStatusChange={handleStatusChange}
                                handleActionableChange={handleActionableChange}
                                handleFetchGuidance={handleFetchGuidance}
                                statusUpdatingId={statusUpdatingId}
                                actionableUpdatingId={actionableUpdatingId}
                        guidanceByObligationId={guidanceByObligationId}
                        calendarStatusLabel={calendarStatusLabel}
                              />
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {/* Ingest section below */}
          <div className="mt-10 pt-8 border-t border-slate-200">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <div>
                <h2 className="text-lg font-semibold text-slate-800">Governance Hub ingest</h2>
                <p className="text-slate-500 text-sm mt-0.5">
                  Upload CMS or Medicaid PDF, CSV, or Excel to preview and save obligations.
                </p>
              </div>
              <div className="flex items-center gap-2">
                {isCompliance && (
                  <MedicareResetButton
                    apiBaseUrl={apiBaseUrl}
                    medicareCount={obligations.filter((o) => (o.obligation_type || '') !== 'State Medicaid (SMAC)').length}
                    onDone={async () => {
                      const listRes = await fetch(`${apiBaseUrl}/api/calendar/obligations`, { credentials: 'include' });
                      if (listRes.ok) {
                        const list = await listRes.json();
                        setObligations(Array.isArray(list) ? list : []);
                      }
                    }}
                  />
                )}
                <span className="text-xs font-medium uppercase tracking-wide text-slate-400 bg-slate-100 px-3 py-1.5 rounded-lg border border-slate-200">
                  Preview then save
                </span>
              </div>
            </div>

            <form onSubmit={handlePreviewUpload} className="flex flex-col gap-4">
              <div className="flex flex-wrap items-end gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-medium text-slate-600">Import as</label>
                  <select
                    value={ingestType}
                    onChange={(e) => setIngestType(e.target.value)}
                    className="bg-white border border-slate-200 text-slate-700 rounded-xl px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/30"
                  >
                    <option value="CMS">CMS</option>
                    <option value="MEDICAID">Medicaid</option>
                  </select>
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-medium text-slate-600">File (PDF, CSV, XLSX)</label>
                  <input
                    type="file"
                    accept=".pdf,.csv,.xlsx,.xls"
                    onChange={(event) => setCmsFile(event.target.files?.[0] || null)}
                    className="text-slate-600 text-sm file:mr-3 file:rounded-lg file:border-0 file:bg-indigo-50 file:px-3 file:py-2 file:text-indigo-700 file:text-sm file:font-medium"
                  />
                </div>
                <button
                  type="submit"
                  disabled={previewLoading}
                  className="px-4 py-2 rounded-xl bg-gradient-to-r from-indigo-500 to-blue-600 hover:from-indigo-600 hover:to-blue-700 text-white text-sm font-medium shadow-sm disabled:opacity-60 disabled:cursor-not-allowed transition"
                >
                  {previewLoading ? 'Generating preview...' : 'Generate Preview'}
                </button>
                {previewError && (
                  <span className="text-sm text-red-600">{previewError}</span>
                )}
              </div>
            </form>

            {!previewLoading && previewItems.length > 0 && (
              <div className="mt-6 space-y-6">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  {previewSummary && (
                    <p className="text-sm text-slate-500">
                      Total rows: {previewSummary.total_rows} — Accepted (with date): {previewSummary.accepted_count} — Accepted (no date): {previewSummary.accepted_no_date_count} — Rejected: {previewSummary.rejected_count}
                    </p>
                  )}
                  <div className="flex items-center gap-3 flex-wrap">
                    <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={showRawText}
                        onChange={(e) => setShowRawText(e.target.checked)}
                        className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      Raw text inspector
                    </label>
                    <button
                      type="button"
                      disabled={saving || (acceptedExactPreview.length === 0 && acceptedManualPreview.length === 0)}
                      onClick={handleSaveReviewed}
                      title={acceptedExactPreview.length === 0 && acceptedManualPreview.length === 0 ? 'Generate a preview first' : 'Save accepted rows to calendar'}
                      className="px-4 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium shadow-sm disabled:opacity-60 disabled:cursor-not-allowed transition"
                    >
                      {saving ? 'Saving...' : 'Save reviewed obligations'}
                    </button>
                    {saveMessage && <span className="text-sm text-emerald-600">{saveMessage}</span>}
                  </div>
                </div>

                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                  <h3 className="text-sm font-semibold text-slate-700 p-3 border-b border-slate-200 bg-slate-50/80">
                    Accepted with valid dates ({acceptedExactPreview.length})
                  </h3>
                  {acceptedExactPreview.length === 0 ? (
                    <p className="p-3 text-sm text-slate-500">No rows with exact due dates.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm text-slate-700">
                        <thead>
                          <tr className="text-left text-slate-500 border-b border-slate-200 bg-slate-50/80">
                            <th className="p-2 font-medium">Due date / range</th>
                            <th className="p-2 font-medium">Status</th>
                            <th className="p-2 font-medium">Title</th>
                            <th className="p-2 font-medium">Authority</th>
                            <th className="p-2 font-medium">Source</th>
                            {showRawText && <th className="p-2 font-medium">Raw text</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {acceptedExactPreview.map((item, index) => {
                            const effective = getEffectiveStatus({ due_date: item.due_date, display_status: '' });
                            const rowBg =
                              effective === 'overdue'
                                ? 'bg-red-50/60'
                                : effective === 'due_soon'
                                  ? 'bg-amber-50/60'
                                  : '';
                            return (
                              <tr key={`acc-${item.title}-${item.due_date}-${index}`} className={`border-b border-slate-100 ${rowBg}`}>
                                <td className="p-2 whitespace-nowrap text-slate-600">
                                  {item.qualifier ? `${item.qualifier}: ` : ''}
                                  {item.due_date ? formatDate(item.due_date) : item.start_date && item.end_date ? `${formatDate(item.start_date)} – ${formatDate(item.end_date)}` : '—'}
                                </td>
                                <td className="p-2">
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-medium ${statusStyle(effective)}`}>
                                    {effectiveStatusLabel(effective)}
                                  </span>
                                </td>
                                <td className="p-2 text-slate-800">{item.title}</td>
                                <td className="p-2 text-slate-600">{item.authority || previewAuthority || '—'}</td>
                                <td className="p-2 text-slate-500">{item.source_document || ''}{item.source_page ? ` p.${item.source_page}` : item.source_page_or_row_ref ? ` ${item.source_page_or_row_ref}` : ''}</td>
                                {showRawText && <td className="p-2 text-slate-400 text-xs max-w-xs truncate" title={item.notes}>{item.notes || '—'}</td>}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                <div className="bg-white border border-amber-200 rounded-xl overflow-hidden shadow-sm">
                  <h3 className="text-sm font-semibold text-slate-700 p-3 border-b border-slate-200 bg-amber-50/50">
                    Accepted — no date ({acceptedManualPreview.length})
                  </h3>
                  {acceptedManualPreview.length === 0 ? (
                    <p className="p-3 text-sm text-slate-500">No rows needing manual date.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm text-slate-700">
                        <thead>
                          <tr className="text-left text-slate-500 border-b border-slate-200 bg-slate-50/80">
                            <th className="p-2 font-medium">Raw date text</th>
                            <th className="p-2 font-medium">Title</th>
                            <th className="p-2 font-medium">Authority</th>
                            <th className="p-2 font-medium">Source</th>
                            {showRawText && <th className="p-2 font-medium">Raw text</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {acceptedManualPreview.map((item, index) => (
                            <tr key={`manual-${item.title}-${index}`} className="border-b border-slate-100">
                              <td className="p-2 text-amber-700">{item.raw_date_text || '—'}</td>
                              <td className="p-2 text-slate-800">{item.title}</td>
                              <td className="p-2 text-slate-600">{item.authority || previewAuthority || '—'}</td>
                              <td className="p-2 text-slate-500">{item.source_document || ''}{item.source_page ? ` p.${item.source_page}` : item.source_page_or_row_ref ? ` ${item.source_page_or_row_ref}` : ''}</td>
                              {showRawText && <td className="p-2 text-slate-400 text-xs max-w-xs truncate" title={item.notes}>{item.notes || '—'}</td>}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                  <h3 className="text-sm font-semibold text-slate-700 p-3 border-b border-slate-200 bg-slate-50/80">
                    Rejected ({rejectedPreview.length})
                  </h3>
                  {rejectedPreview.length === 0 ? (
                    <p className="p-3 text-sm text-slate-500">No rejected rows.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm text-slate-700">
                        <thead>
                          <tr className="text-left text-slate-500 border-b border-slate-200 bg-slate-50/80">
                            <th className="p-2 font-medium">Title</th>
                            <th className="p-2 font-medium">Reason</th>
                            <th className="p-2 font-medium">Authority</th>
                            <th className="p-2 font-medium">Source</th>
                            {showRawText && <th className="p-2 font-medium">Raw text</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {rejectedPreview.map((item, index) => (
                            <tr key={`rej-${item.title}-${index}`} className="border-b border-slate-100">
                              <td className="p-2 text-slate-800">{item.title}</td>
                              <td className="p-2 text-slate-500">{item.reject_reason || 'Rejected'}</td>
                              <td className="p-2 text-slate-600">{item.authority || previewAuthority || '—'}</td>
                              <td className="p-2 text-slate-500">{item.source_document || ''}{item.source_page ? ` p.${item.source_page}` : item.source_page_or_row_ref ? ` ${item.source_page_or_row_ref}` : ''}</td>
                              {showRawText && <td className="p-2 text-slate-400 text-xs max-w-xs truncate" title={item.notes}>{item.notes || '—'}</td>}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        )}
      </div>

      {selectedItem && (
        <DetailPanel
          item={selectedItem}
          onClose={() => setSelectedItem(null)}
          onOpenInEmbeddedView={onOpenInEmbeddedView}
          formatDateDisplay={formatDateDisplay}
          effectiveStatus={getEffectiveStatus(selectedItem)}
          statusStyle={statusStyle}
          effectiveStatusLabel={effectiveStatusLabel}
          calendarStatusLabel={calendarStatusLabel}
          isCalendarNonActionable={isCalendarNonActionable}
          isCompliance={isCompliance}
          renderObligationAccountable={renderObligationAccountable}
          handleStatusChange={handleStatusChange}
          handleActionableChange={handleActionableChange}
          handleFetchGuidance={handleFetchGuidance}
          statusUpdatingId={statusUpdatingId}
          actionableUpdatingId={actionableUpdatingId}
          guidanceByObligationId={guidanceByObligationId}
          isCalendarObligationCompleted={isCalendarObligationCompleted}
          onObligationUpdated={handleObligationPatched}
        />
      )}

    </div>
  );
}
